import { type CertificationDto, type SkillDto } from "@crowdcoursing/api/openapi";
import { Close } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type ColumnData } from "~types/datagrid";
import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

import { applyNameFilter, TableNameTypeFilters, usePersistedMultiSelectFilter } from "./TableNameTypeFilters";

type ChartView = "skills-pie" | "certs-pie" | "skills-bar" | "certs-bar" | "skills-growth" | "certs-growth";
type ItemLimit = 10 | 25 | 50 | "all";

const CHART_VIEW_STORAGE_KEY = "skillCertificationData.chartView";
const ITEM_LIMIT_STORAGE_KEY = "skillCertificationData.itemLimit";

const validChartViews: ChartView[] = [
  "skills-pie",
  "certs-pie",
  "skills-bar",
  "certs-bar",
  "skills-growth",
  "certs-growth",
];

const validItemLimits: ItemLimit[] = [10, 25, 50, "all"];

const getInitialChartView = (): ChartView => {
  const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
  if (stored && validChartViews.includes(stored as ChartView)) {
    return stored as ChartView;
  }
  return "skills-pie";
};

const getInitialItemLimit = (): ItemLimit => {
  const stored = localStorage.getItem(ITEM_LIMIT_STORAGE_KEY);
  if (stored === "all") {
    return "all";
  }
  const parsed = parseInt(stored || "", 10);
  if (validItemLimits.includes(parsed as ItemLimit)) {
    return parsed as ItemLimit;
  }
  return 25;
};

const truncateLabel = (text: string, maxLength = 45): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}…`;
};

const opaqueTooltipStyle: Highcharts.CSSObject = {
  color: "#1f2937",
  opacity: 1,
};

const opaqueTooltipOptions: Highcharts.TooltipOptions = {
  backgroundColor: "#ffffff",
  borderColor: "#d1d5db",
  borderRadius: 6,
  borderWidth: 1,
  padding: 8,
  shadow: true,
  style: opaqueTooltipStyle,
};

// Aggregate items by name for pie chart
const aggregateByName = (items: { name?: string | null }[] | null | undefined): { name: string; count: number }[] => {
  if (!items) {
    return [];
  }

  const counts: Record<string, number> = {};

  items.forEach((item) => {
    if (item.name) {
      counts[item.name] = (counts[item.name] || 0) + 1;
    }
  });

  return Object.entries(counts)
    .map(([name, count]) => ({ count, name }))
    .sort((a, b) => b.count - a.count);
};

const toCandidatePercentage = (count: number, totalCandidateCount: number | null | undefined): number => {
  if (!totalCandidateCount || totalCandidateCount <= 0) {
    return 0;
  }

  return Math.round((count / totalCandidateCount) * 1000) / 10;
};

// Generate display dates with smart sampling based on range size
const generateDisplayDates = (startDate?: string, endDate?: string): string[] => {
  if (!startDate || !endDate) {
    return [];
  }

  const start = DateTime.fromISO(startDate).startOf("day");
  const end = DateTime.fromISO(endDate).startOf("day");
  const totalDays = Math.floor(end.diff(start, "days").days) + 1;

  // Determine sampling interval based on range size
  let interval = 1;
  if (totalDays > 730) {
    interval = 30;
  } else if (totalDays > 180) {
    interval = 14;
  } else if (totalDays > 60) {
    interval = 7;
  }

  const result: string[] = [];
  let currentDate = start;

  while (currentDate <= end) {
    result.push(currentDate.toFormat("yyyy-MM-dd"));
    currentDate = currentDate.plus({ days: interval });
  }

  // Always include the end date if not already included
  const endStr = end.toFormat("yyyy-MM-dd");
  if (result[result.length - 1] !== endStr) {
    result.push(endStr);
  }

  return result;
};

// Aggregate items by date for cumulative growth within a date range
const aggregateGrowthOverTime = (
  items: { name?: string | null; timestamp?: string }[] | null | undefined,
  startDate?: string,
  endDate?: string,
): { categories: string[]; series: { name: string; data: number[] }[] } => {
  if (!items || items.length === 0) {
    return { categories: [], series: [] };
  }

  const startDateTime = startDate ? DateTime.fromISO(startDate).startOf("day") : null;
  const endDateTime = endDate ? DateTime.fromISO(endDate).endOf("day") : null;

  // Get all unique names (from all items, not just filtered)
  const uniqueNames = new Set<string>();
  items.forEach((item) => {
    if (item.name) {
      uniqueNames.add(item.name);
    }
  });

  // Sort items by timestamp
  const sortedItems = [...items]
    .filter((item) => item.timestamp && item.name)
    .sort((a, b) => {
      const dateA = new Date(a.timestamp || "").getTime();
      const dateB = new Date(b.timestamp || "").getTime();
      return dateA - dateB;
    });

  if (sortedItems.length === 0) {
    return { categories: [], series: [] };
  }

  // Calculate baseline counts (items before start date)
  const baselineCounts: Record<string, number> = {};
  uniqueNames.forEach((name) => {
    baselineCounts[name] = 0;
  });

  // Group by date (day)
  const dateGroups: Record<string, Record<string, number>> = {};

  sortedItems.forEach((item) => {
    const itemDate = DateTime.fromISO(item.timestamp || "");
    const dateStr = itemDate.toFormat("yyyy-MM-dd");

    // Count items before start date as baseline
    if (startDateTime && itemDate < startDateTime) {
      baselineCounts[item.name || ""] = (baselineCounts[item.name || ""] || 0) + 1;
      return;
    }

    // Skip items after end date
    if (endDateTime && itemDate > endDateTime) {
      return;
    }

    if (!dateGroups[dateStr]) {
      dateGroups[dateStr] = {};
    }
    dateGroups[dateStr][item.name || ""] = (dateGroups[dateStr][item.name || ""] || 0) + 1;
  });

  // Get sorted dates with actual data
  const datesWithData = Object.keys(dateGroups).sort();

  // Generate display dates with smart sampling
  const startStr = startDate ? DateTime.fromISO(startDate).toFormat("yyyy-MM-dd") : null;
  const endStr = endDate ? DateTime.fromISO(endDate).toFormat("yyyy-MM-dd") : null;
  const displayDates = generateDisplayDates(startStr ?? undefined, endStr ?? undefined);

  if (displayDates.length === 0) {
    return { categories: [], series: [] };
  }

  // Build cumulative series for each display date
  const seriesData: Record<string, number[]> = {};
  uniqueNames.forEach((name) => {
    seriesData[name] = [];
  });

  displayDates.forEach((displayDate) => {
    // Calculate cumulative for each name up to this date
    uniqueNames.forEach((name) => {
      const baseline = baselineCounts[name] || 0;
      const cumulativeInRange = datesWithData
        .filter((d) => d <= displayDate)
        .reduce((sum, d) => sum + (dateGroups[d]?.[name] || 0), 0);
      seriesData[name].push(baseline + cumulativeInRange);
    });
  });

  // Format dates for display
  const categories = displayDates.map((date) => DateTime.fromISO(date).toFormat("MMM d, yyyy"));

  // Convert to series array, sorted by final count (descending)
  // Only include series that have at least one non-zero value
  const series = Object.entries(seriesData)
    .filter(([, data]) => data.some((v) => v > 0))
    .map(([name, data]) => ({ data, name }))
    .sort((a, b) => {
      const lastA = a.data[a.data.length - 1] || 0;
      const lastB = b.data[b.data.length - 1] || 0;
      return lastB - lastA;
    });

  return { categories, series };
};

interface PieChartProps {
  data: { name: string; count: number }[];
  filterStorageKey: string;
  limit: ItemLimit;
  loading: boolean;
  title: string;
}

interface PieChartRow {
  rowId: string;
  rank: number;
  name: string;
  count: number;
  percentage: number;
}

const PieChart = ({ data, filterStorageKey, limit, loading, title }: PieChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const nameOptions = useMemo(() => [...new Set(data.map((item) => item.name))].sort(), [data]);
  const nameFilter = usePersistedMultiSelectFilter({ storageKey: filterStorageKey, validOptions: nameOptions });
  const limitedData = useMemo(
    () => applyNameFilter(data, nameFilter.validSelected, limit),
    [data, limit, nameFilter.validSelected],
  );

  const total = limitedData.reduce((sum, item) => sum + item.count, 0);

  // Create rows for DataGrid - use index-based rowId to ensure uniqueness
  const rows: PieChartRow[] = useMemo(
    () =>
      limitedData.map((item, index) => ({
        count: item.count,
        name: item.name,
        percentage: total > 0 ? Math.round((item.count / total) * 1000) / 10 : 0,
        rank: index + 1,
        rowId: `${index}-${item.name}`,
      })),
    [limitedData, total],
  );

  const columns = useMemo<GridColDef<PieChartRow>[]>(
    () => [
      {
        field: "rank",
        headerName: "#",
        minWidth: 50,
        width: 60,
      },
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 150,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Count",
        minWidth: 80,
      },
      {
        align: "right",
        field: "percentage",
        flex: 1,
        headerAlign: "right",
        headerName: "% of Total",
        minWidth: 90,
        valueFormatter: ({ value }: { value: number }) => `${value}%`,
      },
    ],
    [],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        animation: false,
        height: 400,
        type: "pie",
      },
      legend: {
        enabled: false, // Disable legend since we're using a table instead
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          center: ["50%", "50%"],
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "{point.percentage:.1f}%",
          },
          showInLegend: false,
          size: "90%",
        },
      },
      series: [
        {
          data: limitedData.map((item) => ({
            name: String(item.name),
            y: Number(item.count),
          })),
          id: title,
          name: "Count",
          type: "pie",
        } as Highcharts.SeriesPieOptions,
      ],
      title: {
        text: title,
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
      },
    }),
    [limitedData, title],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Pie Chart - Left Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <Box sx={{ height: 400 }}>
          <HighchartsReact highcharts={Highcharts} key={title} options={chartOptions} ref={chartRef} />
        </Box>
      </Grid>

      {/* DataGrid Table - Right Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <TableNameTypeFilters
          nameFilterId={`${filterStorageKey}-name-filter`}
          nameOptions={nameOptions}
          namesStorageKey={filterStorageKey}
          onClear={nameFilter.clear}
          onNamesChange={nameFilter.handleChange}
          selectedNames={nameFilter.selected}
        />
        <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
          <DataGrid<PieChartRow>
            columns={columns as ColumnData<PieChartRow>[]}
            rows={rows}
            status={loading ? "pending" : "success"}
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            rowHeight={40}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="client"
            sortingMode="client"
            slots={{
              toolbar: () => null,
            }}
            slotProps={{
              container: {
                sx: { height: "auto", maxHeight: 400, minHeight: "unset" },
              },
              empty: {
                message: "No data available",
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

interface BarChartProps {
  data: { name: string; count: number }[];
  filterStorageKey: string;
  limit: ItemLimit;
  loading: boolean;
  title: string;
  totalCandidateCount: number | null | undefined;
}

interface BarChartRow {
  rowId: string;
  rank: number;
  name: string;
  count: number;
  percentage: number;
}

const BarChart = ({ data, filterStorageKey, limit, loading, title, totalCandidateCount }: BarChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const nameOptions = useMemo(() => [...new Set(data.map((item) => item.name))].sort(), [data]);
  const nameFilter = usePersistedMultiSelectFilter({ storageKey: filterStorageKey, validOptions: nameOptions });
  const limitedData = useMemo(
    () => applyNameFilter(data, nameFilter.validSelected, limit),
    [data, limit, nameFilter.validSelected],
  );

  // Create rows for DataGrid
  const rows: BarChartRow[] = useMemo(
    () =>
      limitedData.map((item, index) => ({
        count: item.count,
        name: item.name,
        percentage: toCandidatePercentage(item.count, totalCandidateCount),
        rank: index + 1,
        rowId: `${index}-${item.name}`,
      })),
    [limitedData, totalCandidateCount],
  );

  const columns = useMemo<GridColDef<BarChartRow>[]>(
    () => [
      {
        field: "rank",
        headerName: "#",
        minWidth: 50,
        width: 60,
      },
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 150,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Count",
        minWidth: 80,
      },
      {
        align: "right",
        field: "percentage",
        flex: 1,
        headerAlign: "right",
        headerName: "% of Candidates",
        minWidth: 90,
        valueFormatter: ({ value }: { value: number }) => `${value}%`,
      },
    ],
    [],
  );

  // Dynamic height based on number of items (single-line truncated labels)
  const chartHeight = Math.max(400, limitedData.length * 22);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        animation: false,
        height: chartHeight,
        type: "bar",
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          colorByPoint: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "{point.y}",
          },
        },
      },
      series: [
        {
          data: limitedData.map((item) => ({
            name: String(item.name),
            y: Number(item.count),
          })),
          id: title,
          name: "Count",
          type: "bar",
        } as Highcharts.SeriesBarOptions,
      ],
      title: {
        text: title,
      },
      tooltip: {
        ...opaqueTooltipOptions,
        formatter: function () {
          const point = this.point;
          const name = point.name ?? "";
          return `<b>${name}</b><br/>Count: <b>${point.y}</b>`;
        },
        useHTML: true,
      },
      xAxis: {
        categories: limitedData.map((item) => item.name),
        labels: {
          formatter: function () {
            return truncateLabel(String(this.value), 40);
          },
        },
        title: {
          text: null,
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "Count",
        },
      },
    }),
    [chartHeight, limitedData, title],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Bar Chart - Left Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <Box sx={{ height: chartHeight, maxHeight: 600, overflow: "auto" }}>
          <HighchartsReact highcharts={Highcharts} key={title} options={chartOptions} ref={chartRef} />
        </Box>
      </Grid>

      {/* DataGrid Table - Right Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <TableNameTypeFilters
          nameFilterId={`${filterStorageKey}-name-filter`}
          nameOptions={nameOptions}
          namesStorageKey={filterStorageKey}
          onClear={nameFilter.clear}
          onNamesChange={nameFilter.handleChange}
          selectedNames={nameFilter.selected}
        />
        <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
          <DataGrid<BarChartRow>
            columns={columns as ColumnData<BarChartRow>[]}
            rows={rows}
            status={loading ? "pending" : "success"}
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            rowHeight={40}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="client"
            sortingMode="client"
            slots={{
              toolbar: () => null,
            }}
            slotProps={{
              container: {
                sx: { height: "auto", maxHeight: 600, minHeight: "unset" },
              },
              empty: {
                message: "No data available",
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

interface GrowthChartProps {
  categories: string[];
  limit: ItemLimit;
  loading: boolean;
  series: { name: string; data: number[] }[];
  storageKey: string;
  title: string;
}

interface GrowthDateDetailItem {
  color: string;
  count: number;
  name: string;
}

const GrowthChart = ({ categories, limit, loading, series, storageKey, title }: GrowthChartProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number | null>(null);

  // Get all available names from series
  const allNames = useMemo(() => series.map((s) => s.name).sort(), [series]);
  const chartColors = useMemo(() => (Highcharts.getOptions().colors ?? []) as string[], []);

  // Initialize filter state from localStorage
  const [selectedNames, setSelectedNames] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Filter series based on selection (show all if none selected)
  const filteredSeries = useMemo(() => {
    const validNames = selectedNames.filter((name) => allNames.includes(name));
    if (validNames.length === 0) {
      return series;
    }
    return series.filter((s) => validNames.includes(s.name));
  }, [series, selectedNames, allNames]);

  // Apply limit after filtering
  const limitedSeries = limit === "all" ? filteredSeries : filteredSeries.slice(0, limit);

  const handleNameChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newNames = typeof value === "string" ? value.split(",") : value;
    setSelectedNames(newNames);
    localStorage.setItem(storageKey, JSON.stringify(newNames));
  };

  const handleClearFilters = () => {
    setSelectedNames([]);
    localStorage.removeItem(storageKey);
  };

  const handlePointClick = useCallback((categoryIndex: number) => {
    setSelectedCategoryIndex(categoryIndex);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedCategoryIndex(null);
  }, []);

  useEffect(() => {
    setSelectedCategoryIndex(null);
  }, [categories, limitedSeries]);

  const selectedDateDetails = useMemo((): { dateLabel: string; items: GrowthDateDetailItem[] } | null => {
    if (selectedCategoryIndex === null) {
      return null;
    }

    const dateLabel = categories[selectedCategoryIndex];
    if (!dateLabel) {
      return null;
    }

    const items = limitedSeries
      .map((s, seriesIndex) => ({
        color: chartColors[seriesIndex % chartColors.length] ?? "#666666",
        count: s.data[selectedCategoryIndex] ?? 0,
        name: s.name,
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);

    if (items.length === 0) {
      return null;
    }

    return { dateLabel, items };
  }, [categories, chartColors, limitedSeries, selectedCategoryIndex]);

  // Fixed chart plot area height + dynamic legend space
  // Chart area (plot + title + axes): ~350px
  // Legend: ~25px per row, assuming ~5 items per row
  const legendRows = Math.ceil(limitedSeries.length / 5);
  const chartPlotHeight = 350;
  const legendHeight = Math.max(50, legendRows * 25);
  const chartHeight = chartPlotHeight + legendHeight;

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        animation: false,
        height: chartHeight,
        type: "line",
      },
      legend: {
        align: "center",
        enabled: true,
        itemWidth: 200,
        labelFormatter: function () {
          return truncateLabel(this.name, 35);
        },
        layout: "horizontal",
        navigation: {
          enabled: false,
        },
        verticalAlign: "bottom",
        width: "100%",
      },
      plotOptions: {
        line: {
          cursor: "pointer",
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4,
              },
            },
          },
          point: {
            events: {
              click: function () {
                handlePointClick(this.index ?? 0);
              },
            },
          },
        },
        series: {
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                handlePointClick(this.index ?? 0);
              },
            },
          },
        },
      },
      series: limitedSeries.map((s, index) => ({
        data: [...s.data],
        id: `${title}-${index}`,
        name: String(s.name),
        type: "line",
      })) as Highcharts.SeriesOptionsType[],
      title: {
        text: title,
      },
      tooltip: {
        enabled: false,
      },
      xAxis: {
        categories,
        title: {
          text: "Date",
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: "Cumulative Count",
        },
      },
    }),
    [categories, chartHeight, handlePointClick, limitedSeries, title],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (series.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 400, justifyContent: "center" }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter controls */}
      <Box sx={{ alignItems: "center", display: "flex", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id={`${storageKey}-filter-label`}>Filter by Name</InputLabel>
          <Select
            id={`${storageKey}-filter`}
            input={<OutlinedInput label="Filter by Name" />}
            labelId={`${storageKey}-filter-label`}
            multiple
            onChange={handleNameChange}
            renderValue={(selected) =>
              selected.length === 0 ? "" : selected.length === 1 ? selected[0] : `${selected.length} selected`
            }
            value={selectedNames}
          >
            {allNames.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={selectedNames.includes(name)} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedNames.length > 0 && (
          <Box sx={{ alignItems: "center", display: "flex" }}>
            <Chip label="Clear filters" onDelete={handleClearFilters} size="small" variant="outlined" />
          </Box>
        )}
      </Box>
      <Box sx={{ minHeight: chartHeight }}>
        <HighchartsReact highcharts={Highcharts} key={title} options={chartOptions} ref={chartRef} />
      </Box>
      <Typography color="text.secondary" sx={{ display: "block", mt: 1 }} variant="caption">
        Click a data point to view cumulative counts for that date.
      </Typography>

      <Drawer
        anchor="right"
        onClose={handleCloseDetails}
        open={selectedDateDetails !== null}
        sx={{
          "& .MuiDrawer-paper": {
            width: { sm: 420, xs: "100%" },
          },
        }}
      >
        {selectedDateDetails ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                p: 2,
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography variant="h6">Cumulative counts</Typography>
                <Typography color="text.secondary" variant="body2">
                  {selectedDateDetails.dateLabel}
                </Typography>
              </Box>
              <IconButton aria-label="Close details" onClick={handleCloseDetails} size="small">
                <Close />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {selectedDateDetails.items.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    alignItems: "flex-start",
                    display: "flex",
                    gap: 1,
                    justifyContent: "space-between",
                    py: 0.75,
                  }}
                >
                  <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, minWidth: 0 }}>
                    <Box
                      aria-hidden
                      sx={{
                        bgcolor: item.color,
                        borderRadius: "50%",
                        flexShrink: 0,
                        height: 10,
                        mt: 0.75,
                        width: 10,
                      }}
                    />
                    <Typography sx={{ wordBreak: "break-word" }} variant="body2">
                      {item.name}
                    </Typography>
                  </Box>
                  <Typography sx={{ flexShrink: 0, fontWeight: 600 }} variant="body2">
                    {item.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </Box>
  );
};

export interface SkillCertificationDataProps {
  skills: SkillDto[] | null | undefined;
  certifications: CertificationDto[] | null | undefined;
  status: Utils.QueryStatus;
  title?: string;
  subtitle?: string;
  timeRange?: DateRangeValue;
  onTimeRangeChange?: (newTimeRange: DateRangeValue) => void;
  totalCandidateCount?: number | null;
}

const defaultTitle = "Skill and Certification Data";
const defaultSubtitle = "Platform-wide skills and certifications breakdown";

export const SkillCertificationData = ({
  skills,
  certifications,
  status,
  title = defaultTitle,
  subtitle = defaultSubtitle,
  timeRange,
  onTimeRangeChange,
  totalCandidateCount,
}: SkillCertificationDataProps) => {
  const [chartView, setChartView] = useState<ChartView>(getInitialChartView);
  const [itemLimit, setItemLimit] = useState<ItemLimit>(getInitialItemLimit);
  const loading = status === "pending";

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, newView);
  };

  const handleItemLimitChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    const newLimit: ItemLimit = value === "all" ? "all" : (parseInt(value, 10) as ItemLimit);
    setItemLimit(newLimit);
    localStorage.setItem(ITEM_LIMIT_STORAGE_KEY, String(newLimit));
  };

  // Date range strings for filtering growth charts
  const startDateStr = timeRange?.startDate?.toISO() ?? undefined;
  const endDateStr = timeRange?.endDate?.toISO() ?? undefined;

  // Aggregated data for pie charts
  const skillsAggregated = useMemo(() => aggregateByName(skills), [skills]);
  const certsAggregated = useMemo(() => aggregateByName(certifications), [certifications]);

  // Growth data for line charts (filtered by date range)
  const skillsGrowth = useMemo(
    () => aggregateGrowthOverTime(skills, startDateStr, endDateStr),
    [skills, startDateStr, endDateStr],
  );
  const certsGrowth = useMemo(
    () => aggregateGrowthOverTime(certifications, startDateStr, endDateStr),
    [certifications, startDateStr, endDateStr],
  );

  // Check if current view is a growth chart
  const isGrowthChart = chartView === "skills-growth" || chartView === "certs-growth";

  const renderChart = () => {
    switch (chartView) {
      case "skills-pie":
        return (
          <PieChart
            data={skillsAggregated}
            filterStorageKey="skillCertificationData.skillsPie.tableNames"
            limit={itemLimit}
            loading={loading}
            title="Skills Distribution"
          />
        );
      case "certs-pie":
        return (
          <PieChart
            data={certsAggregated}
            filterStorageKey="skillCertificationData.certsPie.tableNames"
            limit={itemLimit}
            loading={loading}
            title="Certifications Distribution"
          />
        );
      case "skills-bar":
        return (
          <BarChart
            data={skillsAggregated}
            filterStorageKey="skillCertificationData.skillsBar.tableNames"
            limit={itemLimit}
            loading={loading}
            title="Skills Breakdown"
            totalCandidateCount={totalCandidateCount}
          />
        );
      case "certs-bar":
        return (
          <BarChart
            data={certsAggregated}
            filterStorageKey="skillCertificationData.certsBar.tableNames"
            limit={itemLimit}
            loading={loading}
            title="Certifications Breakdown"
            totalCandidateCount={totalCandidateCount}
          />
        );
      case "skills-growth":
        return (
          <GrowthChart
            categories={skillsGrowth.categories}
            limit={itemLimit}
            loading={loading}
            series={skillsGrowth.series}
            storageKey="skillCertificationData.skillsGrowth.selectedNames"
            title="Skills Growth Over Time"
          />
        );
      case "certs-growth":
        return (
          <GrowthChart
            categories={certsGrowth.categories}
            limit={itemLimit}
            loading={loading}
            series={certsGrowth.series}
            storageKey="skillCertificationData.certsGrowth.selectedNames"
            title="Certifications Growth Over Time"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      {/* How to Read This Data */}
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <strong>Distribution (Pie):</strong> Shows the proportional breakdown of skills or certifications across all
          candidates.
          <br />
          <strong>Breakdown (Bar):</strong> Shows a ranked list of skills or certifications by the number of candidates
          who have them, with the percentage of candidates in scope for each item.
          <br />
          <strong>Growth Over Time:</strong> Tracks how specific skills or certifications have been added to candidate
          profiles over time. Click a data point to open a scrollable breakdown for that date.
          <br />
          <br />
          <em>Use these views to understand what capabilities exist in your talent pool.</em>
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography sx={{ mb: 0.5 }} variant="h6">
                {title}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {subtitle}
              </Typography>
            </Box>
            <Box sx={{ alignItems: "center", display: "flex", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="skill-cert-view-label">View</InputLabel>
                <Select
                  id="skill-cert-view-select"
                  label="View"
                  labelId="skill-cert-view-label"
                  onChange={handleChartViewChange}
                  value={chartView}
                >
                  <MenuItem value="certs-bar">Certifications Breakdown (Bar)</MenuItem>
                  <MenuItem value="certs-pie">Certifications Distribution (Pie)</MenuItem>
                  <MenuItem value="certs-growth">Certifications Growth Over Time</MenuItem>
                  <MenuItem value="skills-bar">Skills Breakdown (Bar)</MenuItem>
                  <MenuItem value="skills-pie">Skills Distribution (Pie)</MenuItem>
                  <MenuItem value="skills-growth">Skills Growth Over Time</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="skill-cert-limit-label">Show</InputLabel>
                <Select
                  id="skill-cert-limit-select"
                  label="Show"
                  labelId="skill-cert-limit-label"
                  onChange={handleItemLimitChange}
                  value={String(itemLimit)}
                >
                  <MenuItem value="10">Top 10</MenuItem>
                  <MenuItem value="25">Top 25</MenuItem>
                  <MenuItem value="50">Top 50</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
              {isGrowthChart && timeRange && onTimeRangeChange && (
                <PopoverDateRangePicker label="Date Range" onChange={onTimeRangeChange} value={timeRange} />
              )}
            </Box>
          </Box>
        </Grid>

        <Grid size={12}>{renderChart()}</Grid>

        {/* Footer with metadata */}
        <Grid size={12}>
          <Typography color="text.secondary" variant="caption">
            Total Skills: {skills?.length || 0} | Total Certifications: {certifications?.length || 0}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
